import { ReactNode } from 'react';
import {
  ChargeDto,
  ContainerChargeDto,
  ContractInfoDto,
  DeclarationChargeDto,
  ExistingShippingServiceDto,
  InvoiceSummaryDto,
  MatchingServiceDto,
  NoContractDraft,
  PercentageChargeDto,
  ShipmentChargeDto,
  WeightChargeDto,
  ZoneDto,
} from 'src/generated/services/TFinancialApi';
import { IRangeValue } from 'src/presentations/components/molecules/fields/rhf-mui-fields';

export type ModalOpen = 'create' | 'approve' | 'reject' | null;

export enum NoContractStep {
  Step1_Select = 0,
  Step2_Resolution = 1,
  Step3_Summary = 2,
  Step2b_NewService = 3,
}

export interface SelectedShippingServiceItemMain extends ExistingShippingServiceDto {
  validityPeriod?: IRangeValue;
  contractFlowId?: string;
  listZone?: ZoneDto[];
  startDate?: string;
  endDate?: string;
}

export interface SelectedShippingServiceItem {
  existingShippingService?: SelectedShippingServiceItemMain;
  contractInfos?: ContractInfoDto[];
  mismatchedFields?: string[];
}

export interface ShippingServiceVariant {
  contractFlowId: string;
  validityPeriod: IRangeValue;
  startDate: string;
  endDate: string;
  listZone?: Array<{ pol: string; pod: string }>;
  mismatchedFields?: string[]; // Contract-level mismatches
}

export interface GroupedShippingService {
  // Key for identification
  key: string;

  // Service fields from existingShippingService (same for all variants)
  shipperName?: string;
  route?: string;
  consigneeName?: string;
  billingPartyCode?: string;
  modality?: string;
  service?: string;
  type?: string;
  carrier?: string | null;
  incoterm?: string | null;

  // Service-level mismatches (from PartialMatchingServiceDto.mismatchedFields)
  serviceMismatchedFields?: string[];

  // Variants from contractInfos (different validity periods)
  variants: ShippingServiceVariant[];

  // Original item for draft saving
  originalItem?: SelectedShippingServiceItem;
}

export interface IMatchingServiceData {
  matchedService?: MatchingServiceDto;
  unmatchedService?: MatchingServiceDto;
  partialMatchingServices?: SelectedShippingServiceItem[];
}

export interface PercentageTableDataDTO extends PercentageChargeDto {
  validityPeriod?: IRangeValue;
}

export interface InvoiceChargeWithCategory extends Omit<ChargeDto, 'legType'> {
  chargeCategory?: string;
  legType?: string;
  decision?: boolean;
}

export interface NoContractForm {
  verdict: NoContractOption;
  agreementType?: RejectEnum;
  contractChoice: string;
  selectedPartialMatchingService?: SelectedShippingServiceItem;
  selectedContractInfo?: ContractInfoDto; // Selected contract from contractInfos array
  newMatchingService?: SelectedShippingServiceItem;
  containerCharges?: ContainerChargeDto[];
  declarationCharges?: DeclarationChargeDto[];
  shipmentDeclarationCharges?: ShipmentChargeDto[];
  weightCharges?: WeightChargeDto[];
  percentageCharges?: PercentageTableDataDTO[];
  invoiceCharges?: InvoiceChargeWithCategory[];
  reason?: string;
}

export interface INoContractMainProps {
  matchingService: IMatchingServiceData;
  draft: NoContractDraft;
  invoiceSummary: InvoiceSummaryDto;
  defaultContractFlowId: string | null;
  onClose(): void;
}

export interface INoContractContentProps {
  matchingService: IMatchingServiceData;
  draft: NoContractDraft;
  activeStep: NoContractStep;
  isLoadingCharges?: boolean;
  billedEntityId?: number;
  onSuccessSubmit?: () => void;
  onEditingChargeChange?: (isEditing: boolean) => void;
}

export interface INoContractContentStep2Props extends INoContractContentProps {
  isEditable?: boolean;
  isLoadingCharges?: boolean;
  onEditingChargeChange?: (isEditing: boolean) => void;
}

export type NoContractOption = null | 'reject' | 'setup-new' | 'accept';

export interface IOptionContext {
  option: NoContractOption;
  setOption: React.Dispatch<React.SetStateAction<NoContractOption>>;
}

export enum RejectEnum {
  ONE_OFF = 'Accept the invoice as is one-off',
  CREATE_NEW_SHIPPING_SERVICE = 'Accept invoice and create shipping service',
  REJECT = 'Reject it',
}

export interface INoRateCardModalProps {
  open: boolean;
}

export interface ModalFooterProps {
  activeStep: NoContractStep;
  onSuccessSubmit: () => void;
  onSaveDraft: () => void;
  onGoBack: () => void;
  onClose: () => void;
  onSkip: () => void;
  onConfirm: (e: any) => void;
  isEditingCharge?: boolean;
}

export interface INoContractSectionProps {
  title: string;
  description: string;
  children?: ReactNode;
}

export interface INoContractSectionHeaderProps {
  title: string;
  description: string;
  children?: ReactNode;
}

export interface SelectCardProps {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  isChosen?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
  header?: string;
  description?: string;
}

export interface IInformationCalloutProps {
  title?: ReactNode;
  text?: ReactNode;
}

export interface ITableFieldNumberProps {
  name: string;
}
